import React from "react";
import PropTypes from "prop-types";
import {
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";

export default function BondListTable({
  tableColumns,
  tableColSpan,
  loading,
  filteredRows,
  orderBy,
  order,
  onSort,
  isTeamMember,
  showRowCancel,
  showRowEndorse,
  onViewBond,
  onOpenCancel,
  onOpenEndorse,
  capitalizeFirst,
  footer,
}) {
  function renderTableBody() {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={tableColSpan} align="center">
            <CircularProgress size={28} />
          </TableCell>
        </TableRow>
      );
    }
    if (filteredRows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={tableColSpan} align="center">
            No Data Available
          </TableCell>
        </TableRow>
      );
    }
    return filteredRows.map((bond, index) => (
      <TableRow
        key={
          bond.id ??
          bond.pk ??
          bond.bond_number ??
          `bond-${bond.created_at ?? ""}-${index}`
        }
      >
        <TableCell>
          {isTeamMember
            ? bond.bond_number
            : bond.bond_number || "Not Assigned"}
        </TableCell>
        <TableCell>{bond.bond_type}</TableCell>
        <TableCell>{bond.bond_sub_category}</TableCell>
        <TableCell>{bond.principals.join(", ")}</TableCell>
        <TableCell>{bond.estate_name}</TableCell>
        <TableCell>{bond.law_firm_name}</TableCell>
        <TableCell>{bond.attorney_name}</TableCell>
        <TableCell>
          ${Number(bond.bond_amount).toLocaleString()}
        </TableCell>
        <TableCell>
          {isTeamMember
            ? capitalizeFirst(bond.underwriting_status)
            : bond.status}
        </TableCell>
        <TableCell>
          {dayjs(bond.created_at).format("DD MMM YYYY")}
        </TableCell>
        <TableCell align="center">
          <Tooltip
            title={isTeamMember ? "View Bond" : "View Bond Details"}
          >
            <IconButton onClick={() => onViewBond(bond)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          {!isTeamMember && showRowCancel && (
            <Tooltip title="Cancel Bond">
              <IconButton
                color="error"
                onClick={() => onOpenCancel(bond)}
              >
                <CancelIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isTeamMember && showRowEndorse && (
            <Tooltip title="Bond Endorsement">
              <IconButton
                color="secondary"
                onClick={() => onOpenEndorse(bond)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    ));
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
              {tableColumns.map((col) => (
                <TableCell
                  key={col}
                  sortDirection={orderBy === col ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === col}
                    direction={orderBy === col ? order : "asc"}
                    onClick={() => onSort(col)}
                  >
                    {col
                      .replaceAll("_", " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{ "& tr:nth-of-type(odd)": { backgroundColor: "#fafafa" } }}
          >
            {renderTableBody()}
          </TableBody>
        </Table>
      </TableContainer>
      {footer}
    </>
  );
}

BondListTable.propTypes = {
  tableColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  tableColSpan: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  filteredRows: PropTypes.array.isRequired,
  orderBy: PropTypes.string.isRequired,
  order: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  isTeamMember: PropTypes.bool.isRequired,
  showRowCancel: PropTypes.bool.isRequired,
  showRowEndorse: PropTypes.bool.isRequired,
  onViewBond: PropTypes.func.isRequired,
  onOpenCancel: PropTypes.func.isRequired,
  onOpenEndorse: PropTypes.func.isRequired,
  capitalizeFirst: PropTypes.func.isRequired,
  footer: PropTypes.node,
};
